import { userTokensService, TokenInfoResponse } from "./UserTokensService";

export interface TokenUsageData {
  allocated: number;
  used: number;
  available: number;
  usedPercentage: number;
  availablePercentage: number;
  period: {
    start: Date | null;
    end: Date | null;
  };
}

export class UserTokenReportService {
  private static instance: UserTokenReportService;
  private tokenData: TokenUsageData | null = null;
  private subscribers: ((data: TokenUsageData | null) => void)[] = [];

  private constructor() {
    // Subscribe to token service for updates
    userTokensService.subscribe((tokenInfo) => {
      this.processTokenData(tokenInfo);
    });
  }

  public static getInstance(): UserTokenReportService {
    if (!UserTokenReportService.instance) {
      UserTokenReportService.instance = new UserTokenReportService();
    }
    return UserTokenReportService.instance;
  }

  private processTokenData(tokenInfo: TokenInfoResponse): void {
    if (!tokenInfo || typeof tokenInfo.alloc !== 'number') {
      this.tokenData = null;
      this.notifySubscribers();
      return;
    }

    const allocated = tokenInfo.alloc;
    const used = tokenInfo.used;
    const available = tokenInfo.avail;
    
    // Calculate percentages
    const usedPercentage = allocated > 0 ? (used / allocated) * 100 : 0;
    const availablePercentage = allocated > 0 ? (available / allocated) * 100 : 0;

    this.tokenData = {
      allocated,
      used,
      available,
      usedPercentage,
      availablePercentage,
      period: {
        start: tokenInfo.alloc_start,
        end: tokenInfo.alloc_end
      }
    };

    this.notifySubscribers();
  }

  public async refreshTokenData(): Promise<TokenUsageData | null> {
    const tokenInfo = await userTokensService.getTokenInfo();
    if (tokenInfo) {
      this.processTokenData(tokenInfo);
    }
    return this.tokenData;
  }

  public getTokenData(): TokenUsageData | null {
    return this.tokenData;
  }

  // Subscribe to changes
  public subscribe(
    callback: (data: TokenUsageData | null) => void,
  ): () => void {
    this.subscribers.push(callback);
    callback(this.tokenData);

    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  // Notify all subscribers of changes
  private notifySubscribers(): void {
    for (const callback of this.subscribers) {
      callback(this.tokenData);
    }
  }
}

export const userTokenReportService = UserTokenReportService.getInstance();